import SVGIcon from '../../assets/images/svg/SVGIcon';
import {
  TASK_PRIORITY_LABELS,
  TASK_PRIORITY,
  ICON_BUTTON_TYPE,
  ICON_BUTTON_SIZE,
  AVATAR_BORDER,
  STATUS_TYPE,
  ICON_SIZE
} from '../../global/constants';
import { renderStatusIconName, renderPriorityIconName } from '../../helpers/common';
import { rootStore } from '../../mobx/rootStore';
import { dispatchTaskError, dispatchTaskSuccess } from '../../services/taskServices';
import Avatar from '../avatar';
import { Dropdown, DropdownItem } from '../dropdown';
import copy from 'clipboard-copy';
import {
  AvatarDiv,
  BackArrowIcon,
  DropDiv,
  TaskLinkContainer,
  TaskLinkContent,
  TaskLinkPreviewMainDiv,
  TaskLinkWrapper,
  TaskProjectName,
  TaskStatus,
  TaskTabSection,
  TaskTitle
} from './styles';
import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import IconButton from '../button/iconButton';

interface TaskLinkEmbedProps {
  node: {
    attribs: {
      taskid: string;
      projectname: string;
      taskname: string;
      statusname: string;
      priorityname: string;
      url: string;
      assigneeUrls: string;
      assigneeName: string;
    };
  };
}

const TaskLinkEmbed = ({ node }: TaskLinkEmbedProps) => {
  const taskId = node.attribs.taskid || '';
  const url = node.attribs?.url;

  // Mobx store variables
  const { projectStore, taskStore } = rootStore || {};
  const { projects } = projectStore || {};
  const { tasks } = taskStore || {};
  const taskDetails = tasks?.find((task) => task.id === taskId);

  //Other variables
  const history = useHistory();

  const defaultStatus = taskDetails?.project?.statusData?.find((status) => status?.Default);
  const status =
    taskDetails?.project?.statusData?.find((status) => status['_id'] === taskDetails?.statusId) || defaultStatus;
  const priorityName = taskDetails?.priority ? TASK_PRIORITY_LABELS[taskDetails?.priority] : node.attribs.priorityname;
  const priorityType = taskDetails?.priority || TASK_PRIORITY.NO_PRIORITY;
  const priorityIconClass = renderPriorityIconName(priorityType);
  const statusType = status?.Type || STATUS_TYPE.UNSTARTED;
  const statusIconClass = renderStatusIconName(statusType);
  const projectName = taskDetails?.project?.name || node.attribs.projectname;
  const taskName = taskDetails?.name || node.attribs.taskname;
  const statusName = status?.Name || node.attribs.statusname;

  const assigneeUrlArray = node.attribs.assigneeUrls?.split(',') || [];
  const assigneeNameArray = node.attribs.assigneeName?.split(',') || [];

  const assignee =
    taskDetails?.users ||
    assigneeUrlArray?.map((url, index) => ({ profile_photo: url, name: assigneeNameArray[index] || '' }));

  const onClickCopyProjectLink = async (url: string) => {
    await copy(url);
    dispatchTaskSuccess('Task url copied successfully!');
  };

  const onClickCard = useCallback(() => {
    const projectId = url.split('/projects/details/')[1]?.split('/tasks')[0];
    const path = new URL(url).pathname;
    const projectDetails = projects?.find((project) => project.id === projectId);
    if (projectDetails) {
      rootStore.projectStore.setCurrentProject(projectId);
      history.push(path);
    } else dispatchTaskError(`You don't have access for this task`);
  }, [history, projects, url]);

  return (
    <TaskLinkPreviewMainDiv role='button' data-name={taskId} onClick={onClickCard}>
      <TaskLinkContainer>
        <TaskLinkContent>
          <TaskLinkWrapper>
            <TaskProjectName>{projectName || 'TeamCamp Web'}</TaskProjectName>
            <TaskTitle>
              {taskName || 'Task Link'}
              <BackArrowIcon>
                <SVGIcon
                  name='updated-right-arrow-icon'
                  width={ICON_SIZE.LG}
                  height={ICON_SIZE.LG}
                  viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                  fill='var(--text-secondary)'
                />
              </BackArrowIcon>
            </TaskTitle>
          </TaskLinkWrapper>
          <DropDiv>
            <Dropdown
              activeClassName='active-dropdown'
              content={
                <IconButton
                  variant={ICON_BUTTON_TYPE.TERTIARY}
                  size={ICON_BUTTON_SIZE.MEDIUM}
                  iconName='three-dots-horizontal-icon'
                  className='task-link-embed-dropdown'
                />
              }
              trigger='click'>
              <DropdownItem
                label='Copy link'
                iconName='copy-link-icon'
                onClick={() => onClickCopyProjectLink(node.attribs?.url)}
                iconViewBox='0 0 18 18'
              />
            </Dropdown>
          </DropDiv>
        </TaskLinkContent>
        <TaskTabSection>
          {/* Status */}
          <TaskStatus>
            {/* DO CODE FOR STATUS SHOW */}
            <SVGIcon
              name={statusIconClass}
              width={ICON_SIZE.SM}
              height={ICON_SIZE.SM}
              viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
              fill='none'
            />
            <span className='status-name'>{statusName || 'TODO'}</span>
          </TaskStatus>
          {/* Priority */}
          {taskDetails?.project?.priority?.enabled && (
            <TaskStatus>
              <SVGIcon
                name={priorityIconClass}
                width={ICON_SIZE.SM}
                height={ICON_SIZE.SM}
                viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
                fill='none'
              />
              <span className='status-name'>{priorityName || 'No Priority'}</span>
            </TaskStatus>
          )}
          {/* Assignee */}
          <TaskStatus className='task-assignee task-avatar'>
            {assignee?.map((item, index) => (
              <AvatarDiv marginLeft={index > 0 ? '-12px' : 'var(--space-0)'}>
                <Avatar
                  imgSrc={item.profile_photo || ''}
                  name={item.name}
                  size={18}
                  borderVariant={AVATAR_BORDER.NONE}
                  avatarSingleLetter={true}
                />
              </AvatarDiv>
            ))}
            {assignee?.length === 1 && <span className='status-name'>{assignee[0]?.name || 'Unknown'}</span>}
          </TaskStatus>
        </TaskTabSection>
      </TaskLinkContainer>
    </TaskLinkPreviewMainDiv>
  );
};

export default TaskLinkEmbed;
